package it.neckar.lizergy.model.financial.quote.query

import it.neckar.commons.tags.Tag
import it.neckar.commons.tags.Tags
import it.neckar.lizergy.model.financial.quote.Optionality
import it.neckar.lizergy.model.financial.quote.QuoteElement

/**
 * Describes a query for relevance categories.
 * The query can be used to calculate sums.
 */
interface CalculationRelevanceQuery : Query {
  /**
   * Creates a new query that connects both queries using "and"
   */
  operator fun plus(otherQuery: CalculationRelevanceQuery): CalculationRelevanceQuery {
    return AndCalculationRelevanceQuery(listOf(this, otherQuery))
  }

  companion object {
    /**
     * Selects all items. Even the optional.
     * Must not be used for calculations.
     */
    val allIncludingOptional: CalculationRelevanceQuery = object : CalculationRelevanceQuery {
      override fun matches(quoteElement: QuoteElement): Boolean {
        return true
      }
    }

    val onlyOptional: CalculationRelevanceQuery = object : CalculationRelevanceQuery {
      override fun matches(quoteElement: QuoteElement): Boolean {
        return quoteElement.optionality == Optionality.Optional
      }
    }

    /**
     * Only mandatory items are returned (not optional)
     *
     *
     * This query should typically be used to calculate the sum
     */
    val onlyMandatory: CalculationRelevanceQuery = object : CalculationRelevanceQuery {
      override fun matches(quoteElement: QuoteElement): Boolean {
        return (quoteElement.optionality == Optionality.Mandatory)
      }
    }

    /**
     * The default query for calculations (only contains the mandatory items) - not the optional!
     */
    val default: CalculationRelevanceQuery = onlyMandatory


    fun onlyMandatoryWithoutTag(tag: Tag): CalculationRelevanceQuery {
      return object : CalculationRelevanceQuery {
        override fun matches(quoteElement: QuoteElement): Boolean {
          return onlyMandatory.matches(quoteElement) && (quoteElement.relevanceOrNull()?.contains(tag)?.not() == true)
        }
      }
    }

  }
}

/**
 * Returns true if all the delegates returns true.
 * Returns false if any of the delegates returns false.
 */
data class AndCalculationRelevanceQuery(override val delegates: List<CalculationRelevanceQuery>) : AndQuery, CalculationRelevanceQuery

/**
 * Returns true if all the delegates return false.
 * Returns false if at least one of the delegates returns true
 */
data class NoneCalculationRelevanceQuery(override val delegates: List<CalculationRelevanceQuery>) : NoneQuery, CalculationRelevanceQuery


/**
 * Returns a new query for this category
 */
fun Tag.asQuery(): CalculationRelevanceQuery {
  return QueryByCalculationRelevanceCategory(this)
}

class QueryByCalculationRelevanceCategory(val tag: Tag) : CalculationRelevanceQuery {
  override fun matches(quoteElement: QuoteElement): Boolean {
    return quoteElement.relevanceOrNull()?.contains(tag) == true
  }
}

fun Tags.asQuery(): CalculationRelevanceQuery {
  return QueryByCalculationRelevanceCategories(this)
}

class QueryByCalculationRelevanceCategories(val tags: Tags) : CalculationRelevanceQuery {
  override fun matches(quoteElement: QuoteElement): Boolean {
    if (tags.isEmpty()) return quoteElement.relevanceOrNull()?.isEmpty() == true
    return tags.tags.all {
      quoteElement.relevanceOrNull()?.contains(it) == true
    }
  }
}
